import axios, { type AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';

const SESSION_KEY = 'hubspot_session_token';
const LOGIN_VERIFIER_KEY = 'hubspot_login_verifier';
// Netlify proxies session endpoints to Render so the cookie belongs to the app
// domain. Other API traffic keeps its direct connection for streaming/downloads.
const baseURL = import.meta.env.DEV ? import.meta.env.VITE_API_URL || '/api' : '/api';
const client = axios.create({
  baseURL,
  withCredentials: true,
  headers: { 'X-Quote-Tool-Request': '1' },
});

export const SESSION_EXPIRED_MESSAGE = 'Je sessie is verlopen.';
const LOGIN_ERRORS: Record<string, string> = {
  portal: 'Dit HubSpot-account heeft geen toegang tot de offertetool.',
  failed: 'Inloggen via HubSpot is niet gelukt. Probeer het opnieuw.',
};

interface HubSpotSessionUser {
  id: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  hubspotPortalId: string;
}
interface SessionResponse {
  token: string;
  user: HubSpotSessionUser;
}

type SessionState = { status: 'loading' | 'ready' | 'blocked'; message?: string };
let state: SessionState = { status: 'loading' };
const listeners = new Set<() => void>();
let initialization: Promise<void> | undefined;
let renewal: Promise<string | null> | undefined;

export function getHubSpotSessionToken(): string | null {
  return sessionStorage.getItem(SESSION_KEY);
}

export function getHubSpotSessionState(): SessionState {
  return state;
}
export function subscribeHubSpotSession(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function updateState(next: SessionState) {
  state = next;
  listeners.forEach((listener) => listener());
}

export function clearHubSpotSession(message?: string) {
  sessionStorage.removeItem(SESSION_KEY);
  updateState({ status: 'blocked', message });
}

function rememberUser(user: HubSpotSessionUser) {
  localStorage.setItem('hubspot_portal_id', user.hubspotPortalId);
  localStorage.setItem(
    'hubspot_user',
    JSON.stringify({
      id: user.id.replace(/^hubspot_user_/, ''),
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
    })
  );
}

function storeSession({ token, user }: SessionResponse) {
  sessionStorage.setItem(SESSION_KEY, token);
  rememberUser(user);
}

const responseStatus = (error: unknown) =>
  (error as { response?: { status?: number } }).response?.status;

async function restoreSession() {
  try {
    // Cookies are shared with new tabs and direct visits. Prefer them over
    // an older tab token, which may belong to a previous HubSpot user.
    storeSession((await client.post<SessionResponse>('/auth/refresh')).data);
  } catch (error) {
    const token = getHubSpotSessionToken();
    if (!token || responseStatus(error) !== 401) throw error;
    // Iframes that block cookies keep working with their tab token until it expires.
    const { data } = await client.get<{ user: HubSpotSessionUser }>('/auth/me', {
      headers: { Authorization: `Bearer ${token}` },
    });
    rememberUser(data.user);
  }
}

/** Shared promise prevents React StrictMode from consuming a one-use ticket twice. */
export function initializeHubSpotSession(): Promise<void> {
  if (initialization) return initialization;
  initialization = (async () => {
    // HubSpot appends #<parent origin> to the iframe URL. That suffix is host
    // metadata, not part of the one-use ticket, and must survive URL cleanup.
    const [launchParameters, ...hostMetadata] = window.location.hash.slice(1).split('#');
    const fragment = new URLSearchParams(launchParameters);
    const ticket = fragment.get('hubspotTicket');
    const loginError = fragment.get('loginError');
    if (ticket || loginError) {
      fragment.delete('hubspotTicket');
      fragment.delete('loginError');
      const hash = [fragment.toString(), ...hostMetadata].filter(Boolean).join('#');
      window.history.replaceState(
        window.history.state,
        '',
        `${window.location.pathname}${window.location.search}${hash ? `#${hash}` : ''}`
      );
      sessionStorage.removeItem(SESSION_KEY);
    }
    const verifier = sessionStorage.getItem(LOGIN_VERIFIER_KEY);
    sessionStorage.removeItem(LOGIN_VERIFIER_KEY);
    localStorage.removeItem('hubspot_token');
    if (loginError) {
      clearHubSpotSession(LOGIN_ERRORS[loginError] ?? LOGIN_ERRORS.failed);
      return;
    }
    try {
      if (ticket) {
        const { data } = await client.post<SessionResponse>(
          '/auth/hubspot/session',
          verifier ? { ticket, verifier } : { ticket }
        );
        storeSession(data);
      } else {
        await restoreSession();
      }
      updateState({ status: 'ready' });
    } catch {
      clearHubSpotSession(ticket && verifier ? LOGIN_ERRORS.failed : undefined);
    }
  })();
  return initialization;
}

const base64Url = (bytes: Uint8Array) =>
  btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');

/**
 * Signs in through HubSpot in this tab. The verifier stays in this tab's
 * sessionStorage, so only this tab can redeem the ticket HubSpot sends back.
 */
export async function startHubSpotLogin(): Promise<void> {
  const verifier = base64Url(crypto.getRandomValues(new Uint8Array(32)));
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  sessionStorage.setItem(LOGIN_VERIFIER_KEY, verifier);
  const params = new URLSearchParams({
    challenge: base64Url(new Uint8Array(digest)),
    next: `${window.location.pathname}${window.location.search}`,
  });
  window.location.assign(`${baseURL}/auth/hubspot/login?${params}`);
}

/** Renews the API token from the session cookie; concurrent callers share one request. */
export function refreshHubSpotSession(): Promise<string | null> {
  renewal ??= client
    .post<SessionResponse>('/auth/refresh')
    .then(({ data }) => {
      storeSession(data);
      return data.token;
    })
    .catch(() => {
      clearHubSpotSession(SESSION_EXPIRED_MESSAGE);
      return null;
    })
    .finally(() => {
      renewal = undefined;
    });
  return renewal;
}

/** Axios response handler: renew an expired API token once and repeat the request. */
export async function retryWithRenewedSession(error: AxiosError, api: AxiosInstance) {
  const config = error.config as
    | (InternalAxiosRequestConfig & { sessionRenewed?: boolean })
    | undefined;
  if (error.response?.status !== 401 || !config) throw error;
  if (config.sessionRenewed) {
    clearHubSpotSession(SESSION_EXPIRED_MESSAGE);
    throw error;
  }
  if (!(await refreshHubSpotSession())) throw error;
  config.sessionRenewed = true;
  return api.request(config);
}

/** Clears the tab only after the server revoked the cookie session, which would otherwise return. */
export async function logoutHubSpotSession(): Promise<void> {
  await client.post('/auth/logout');
  localStorage.removeItem('hubspot_user');
  clearHubSpotSession('Je bent uitgelogd.');
}

export async function openStandaloneApp(): Promise<void> {
  // Open synchronously from the click to avoid popup blockers during the request.
  const popup = window.open('about:blank', '_blank');
  if (!popup) throw new Error('Sta pop-ups toe om de app in een nieuw tabblad te openen.');
  popup.opener = null;
  const handoff = () => {
    const token = getHubSpotSessionToken();
    return client.post<{ ticket: string }>(
      '/auth/hubspot/handoff',
      {},
      {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      }
    );
  };
  try {
    let response;
    try {
      response = await handoff();
    } catch (error) {
      if (responseStatus(error) !== 401 || !(await refreshHubSpotSession())) throw error;
      response = await handoff();
    }
    const url = new URL(
      `${window.location.pathname}${window.location.search}`,
      window.location.origin
    );
    url.hash = `hubspotTicket=${encodeURIComponent(response.data.ticket)}`;
    popup.location.replace(url.toString());
  } catch (error) {
    popup.close();
    throw error;
  }
}
